import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader, EmptyState, ErrorState } from '@ui-kit';
import { useCacheList } from '../hooks/useCacheList';
import { useCacheOverview } from '../hooks/useCacheOverview';
import { useEvictCache } from '../hooks/useEvictCache';
import { useEvictAll } from '../hooks/useEvictAll';
import { CacheTierBadge } from '../components/CacheTierBadge';
import { HitRateGauge } from '../components/HitRateGauge';
import { EvictConfirmDialog } from '../components/EvictConfirmDialog';
import { CacheKeyspaceDrawer } from '../components/CacheKeyspaceDrawer';
import { CacheCommonUsagePanel } from '../components/CacheCommonUsagePanel';
import { ButtonGuard } from '../../_shared/components/ButtonGuard';
import type { CacheSummary, CacheTier, ApiError } from '../types/cacheAdmin';

/** 캐시 카드 스켈레톤 */
function CacheCardSkeleton() {
  return (
    <div
      aria-hidden="true"
      style={{
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--spacing-4)',
        background: 'var(--color-surface-background)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-3)',
        minHeight: 180,
      }}
    >
      {[80, 60, 100, 70, 90].map((w, i) => (
        <div
          key={i}
          style={{
            height: i === 2 ? 8 : 12,
            width: `${w}%`,
            background: 'var(--color-surface-elevated)',
            borderRadius: 'var(--radius-sm)',
            animation: 'pulse 1.5s ease-in-out infinite',
            animationDelay: `${i * 0.1}s`,
          }}
        />
      ))}
    </div>
  );
}

/** TTL 초 → 사람 읽기 형식 */
function formatTtl(seconds: number | null): string {
  if (seconds === null) return '-';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  return `${Math.floor(seconds / 3600)}h`;
}

/**
 * CacheAdminPage — 캐시 관리 메인 페이지 (`/cache-admin`)
 *
 * 5상태:
 *   - loading: 캐시 카드 skeleton 5개
 *   - empty: EmptyState (운영 환경에서는 거의 발생 안 함)
 *   - error: ErrorState (403은 [홈으로], 기타는 [재시도])
 *   - default: 캐시 카드 그리드 + 개요 배너 + 필터
 *   - dialog: EvictConfirmDialog (단건/전체 evict) + CacheKeyspaceDrawer
 *
 * 권한: CACHE_READ 조회 / CACHE_EVICT evict (ButtonGuard)
 */
export function CacheAdminPage() {
  const { t } = useTranslation();

  // 필터 상태
  const [tierFilter, setTierFilter] = useState<CacheTier | ''>('');
  const [keywordInput, setKeywordInput] = useState('');
  const [appliedFilters, setAppliedFilters] = useState<{
    tier: CacheTier | '';
    keyword: string;
  }>({ tier: '', keyword: '' });

  // dialog / drawer 상태
  const [evictTarget, setEvictTarget] = useState<CacheSummary | null>(null);
  const [showEvictAll, setShowEvictAll] = useState(false);
  const [drawerCache, setDrawerCache] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // 쿼리
  const listQuery = useCacheList(appliedFilters);
  const overviewQuery = useCacheOverview();
  const evictMutation = useEvictCache();
  const evictAllMutation = useEvictAll();

  const caches = listQuery.data ?? [];
  const isLoading = listQuery.isLoading;
  const isError = listQuery.isError;
  const isEmpty = !isLoading && !isError && caches.length === 0;
  const showContent = !isLoading && !isError && caches.length > 0;

  const overview = overviewQuery.data;
  const l2Status = overview?.l2?.status ?? 'DISABLED';

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 5000);
  }, []);

  const handleSearch = useCallback(() => {
    setAppliedFilters({ tier: tierFilter, keyword: keywordInput });
  }, [tierFilter, keywordInput]);

  const handleReset = useCallback(() => {
    setTierFilter('');
    setKeywordInput('');
    setAppliedFilters({ tier: '', keyword: '' });
  }, []);

  const handleRefresh = useCallback(() => {
    void listQuery.refetch();
    void overviewQuery.refetch();
  }, [listQuery, overviewQuery]);

  const handleEvictSingleConfirm = useCallback(() => {
    if (!evictTarget) return;
    evictMutation.mutate(evictTarget.name, {
      onSuccess: () => {
        showToast(t('cacheAdmin.toast.evicted', { name: evictTarget.name }));
        setEvictTarget(null);
      },
      onError: (err) => {
        const apiErr = err as unknown as ApiError;
        showToast(
          t(`cacheAdmin.error.${apiErr.code}`, {
            defaultValue: t('cacheAdmin.error.network'),
          })
        );
        setEvictTarget(null);
      },
    });
  }, [evictTarget, evictMutation, showToast, t]);

  const handleEvictAllConfirm = useCallback(() => {
    evictAllMutation.mutate(undefined, {
      onSuccess: (result) => {
        showToast(
          t('cacheAdmin.toast.evictedAll', { count: result.evictedCount })
        );
        if (result.warning) {
          setTimeout(() => showToast(result.warning!), 5500);
        }
        setShowEvictAll(false);
      },
      onError: (err) => {
        const apiErr = err as unknown as ApiError;
        showToast(
          t(`cacheAdmin.error.${apiErr.code}`, {
            defaultValue: t('cacheAdmin.error.network'),
          })
        );
        setShowEvictAll(false);
      },
    });
  }, [evictAllMutation, showToast, t]);

  // 개요 배너 — L2 상태 표시
  const l2StatusLabel =
    l2Status === 'ACTIVE'
      ? t('cacheAdmin.page.l2.active')
      : l2Status === 'UNAVAILABLE'
      ? t('cacheAdmin.page.l2.unavailable')
      : t('cacheAdmin.page.l2.disabled');

  const l2StatusColor =
    l2Status === 'ACTIVE'
      ? 'var(--color-success, #16a34a)'
      : l2Status === 'UNAVAILABLE'
      ? 'var(--color-danger)'
      : 'var(--color-text-muted)';

  return (
    <div style={{ padding: 0 }}>
      {/* 페이지 헤더 */}
      <PageHeader
        title={t('cacheAdmin.page.title')}
        breadcrumb={<span>{t('cacheAdmin.page.title')}</span>}
        actions={
          <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center' }}>
            <button
              type="button"
              onClick={handleRefresh}
              disabled={isLoading}
              aria-label={t('cacheAdmin.button.refresh')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: 'var(--spacing-2) var(--spacing-3)',
                background: 'var(--color-surface-elevated)',
                color: 'var(--color-text-primary)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 500,
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-family-sans)',
                opacity: isLoading ? 0.6 : 1,
              }}
            >
              {isLoading ? '...' : t('cacheAdmin.button.refresh')}
            </button>

            <ButtonGuard requirePermission="CACHE_EVICT">
              {({ disabled, reason }) => (
                <button
                  type="button"
                  disabled={disabled}
                  title={reason}
                  onClick={() => setShowEvictAll(true)}
                  aria-label={t('cacheAdmin.button.evictAll')}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: 'var(--spacing-2) var(--spacing-3)',
                    background: disabled ? 'var(--color-surface-muted)' : 'var(--color-danger)',
                    color: disabled ? 'var(--color-text-muted)' : 'var(--color-text-inverse)',
                    border: `1px solid ${disabled ? 'var(--color-surface-border)' : 'var(--color-danger)'}`,
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 600,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    fontFamily: 'var(--font-family-sans)',
                    opacity: disabled ? 0.6 : 1,
                  }}
                >
                  {t('cacheAdmin.button.evictAll')}
                </button>
              )}
            </ButtonGuard>
          </div>
        }
      />

      {/* 개요 배너 */}
      {overview && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 'var(--spacing-2)',
            padding: 'var(--spacing-3) var(--spacing-4)',
            background: 'var(--color-surface-muted)',
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
            marginBottom: 'var(--spacing-4)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          <span style={{ color: 'var(--color-text-secondary)' }}>
            {overview.l1.status === 'ACTIVE' &&
              t('cacheAdmin.page.subtitle', {
                l1Count: overview.l1.cacheCount,
                totalEntries: (overview.l1.totalEntries ?? 0).toLocaleString(),
                avgHitRate:
                  overview.l1.avgHitRate != null
                    ? `${Math.round(overview.l1.avgHitRate * 100)}%`
                    : '-',
              })}
          </span>
          <span
            style={{
              color: l2StatusColor,
              fontWeight: 500,
              fontSize: 'var(--font-size-xs)',
            }}
          >
            {l2StatusLabel}
          </span>
        </div>
      )}

      {/* 필터 바 */}
      <div
        role="search"
        aria-label={t('cacheAdmin.page.title')}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--spacing-3)',
          padding: 'var(--spacing-3) var(--spacing-4)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          background: 'var(--color-surface-muted)',
          marginBottom: 'var(--spacing-4)',
          alignItems: 'flex-end',
        }}
      >
        {/* 계층 */}
        <label
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-1)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-secondary)',
          }}
        >
          {t('cacheAdmin.filter.tier')}
          <select
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value as CacheTier | '')}
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              cursor: 'pointer',
            }}
          >
            <option value="">{t('cacheAdmin.filter.tierAll')}</option>
            <option value="L1">L1 (Caffeine)</option>
            <option value="L2">L2 (Redis)</option>
          </select>
        </label>

        {/* 검색 */}
        <label
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-1)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-secondary)',
          }}
        >
          {t('cacheAdmin.filter.keyword')}
          <input
            type="text"
            value={keywordInput}
            onChange={(e) => setKeywordInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSearch();
            }}
            placeholder="캐시명..."
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              fontFamily: 'var(--font-family-sans)',
              minWidth: 180,
            }}
          />
        </label>

        <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginLeft: 'auto' }}>
          <button
            type="button"
            onClick={handleSearch}
            disabled={isLoading}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-brand-primary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            검색
          </button>
          <button
            type="button"
            onClick={handleReset}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            초기화
          </button>
        </div>
      </div>

      {/* 콘텐츠 영역 — 5상태 */}

      {/* loading — skeleton 카드 그리드 */}
      {isLoading && (
        <div
          aria-label={t('cacheAdmin.list.loadingAriaLabel')}
          aria-busy="true"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 'var(--spacing-4)',
          }}
        >
          {Array.from({ length: 5 }).map((_, i) => (
            <CacheCardSkeleton key={i} />
          ))}
        </div>
      )}

      {/* error */}
      {isError && (
        <div
          style={{
            padding: 'var(--spacing-6)',
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          <ErrorState
            title={t('cacheAdmin.error.loadFailed')}
            message={
              (listQuery.error as unknown as ApiError)?.code === 'AUTH-403-001'
                ? t('cacheAdmin.error.forbidden')
                : ((listQuery.error as unknown as ApiError)?.message ??
                  t('cacheAdmin.error.network'))
            }
            onRetry={
              (listQuery.error as unknown as ApiError)?.code === 'AUTH-403-001'
                ? undefined
                : () => void listQuery.refetch()
            }
          />
        </div>
      )}

      {/* empty */}
      {isEmpty && (
        <div
          style={{
            padding: 'var(--spacing-6)',
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          <EmptyState
            title={t('cacheAdmin.list.empty.title')}
            description={t('cacheAdmin.list.empty.description')}
          />
        </div>
      )}

      {/* default — 캐시 카드 그리드 */}
      {showContent && (
        <div
          role="region"
          aria-label={t('cacheAdmin.list.tableAriaLabel')}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 'var(--spacing-4)',
          }}
        >
          {caches.map((cache) => (
            <CacheCard
              key={cache.name}
              cache={cache}
              onViewKeys={() => setDrawerCache(cache.name)}
              onEvict={() => setEvictTarget(cache)}
            />
          ))}
        </div>
      )}

      {/* 공통 모듈 시연 패널 */}
      <CacheCommonUsagePanel />

      {/* 단건 evict 확인 dialog */}
      {evictTarget && (
        <EvictConfirmDialog
          mode="single"
          cacheName={evictTarget.name}
          isPending={evictMutation.isPending}
          onConfirm={handleEvictSingleConfirm}
          onCancel={() => setEvictTarget(null)}
        />
      )}

      {/* 전체 evict 확인 dialog */}
      {showEvictAll && (
        <EvictConfirmDialog
          mode="all"
          isPending={evictAllMutation.isPending}
          onConfirm={handleEvictAllConfirm}
          onCancel={() => setShowEvictAll(false)}
        />
      )}

      {/* 키스페이스 drawer */}
      {drawerCache && (
        <CacheKeyspaceDrawer
          cacheName={drawerCache}
          onClose={() => setDrawerCache(null)}
          onEvictSuccess={(name) => {
            showToast(t('cacheAdmin.toast.evicted', { name }));
            setDrawerCache(null);
          }}
        />
      )}

      {/* 토스트 */}
      {toast && (
        <div
          role="alert"
          aria-live="polite"
          style={{
            position: 'fixed',
            bottom: 'var(--spacing-6)',
            right: 'var(--spacing-6)',
            zIndex: 1100,
            background: 'var(--color-surface-overlay, #1e1e2e)',
            color: 'var(--color-text-inverse)',
            padding: 'var(--spacing-3) var(--spacing-5)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            boxShadow: 'var(--shadow-lg)',
            maxWidth: 400,
          }}
        >
          {toast}
        </div>
      )}

      {/* 반응형 + 애니메이션 스타일 */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @media (max-width: 768px) {
          /* 단일 컬럼 */
        }
      `}</style>
    </div>
  );
}

/* ──────────────────────────────────────────────
   CacheCard — 개별 캐시 카드 컴포넌트
   ────────────────────────────────────────────── */

interface CacheCardProps {
  cache: CacheSummary;
  onViewKeys: () => void;
  onEvict: () => void;
}

function CacheCard({ cache, onViewKeys, onEvict }: CacheCardProps) {
  const { t } = useTranslation();
  const isLowHitRate = cache.hitRate < 0.5;

  return (
    <article
      aria-label={t('cacheAdmin.list.cardAriaLabel', { name: cache.name })}
      style={{
        border: `1px solid ${isLowHitRate ? 'var(--color-warning-border, #fcd34d)' : 'var(--color-surface-border)'}`,
        borderRadius: 'var(--radius-md)',
        padding: 'var(--spacing-4)',
        background: isLowHitRate
          ? 'var(--color-warning-subtle, #fffbeb)'
          : 'var(--color-surface-background)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-3)',
        transition: 'box-shadow 0.15s',
      }}
    >
      {/* 헤더 — tier 배지 + 캐시명 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
        <CacheTierBadge tier={cache.tier} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 700,
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-primary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={cache.name}
          >
            {cache.name}
          </div>
          {(cache.labelKo || cache.labelEn) && (
            <div
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-secondary)',
              }}
            >
              {cache.labelKo}
            </div>
          )}
        </div>
      </div>

      {/* 히트율 게이지 */}
      <HitRateGauge
        hitRate={cache.hitRate}
        hitCount={cache.hitCount}
        missCount={cache.missCount}
      />

      {/* 엔트리 / TTL / eviction */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 'var(--spacing-1)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-secondary)',
        }}
      >
        <div>
          <span>{t('cacheAdmin.card.entries')}: </span>
          <span style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
            {cache.estimatedSize.toLocaleString()}
            {cache.maxSize != null && ` / ${cache.maxSize.toLocaleString()}`}
          </span>
        </div>
        <div>
          <span>{t('cacheAdmin.card.ttl')}: </span>
          <span style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
            {cache.ttlSeconds != null ? formatTtl(cache.ttlSeconds) : '-'}
          </span>
        </div>
        <div>
          <span>{t('cacheAdmin.card.eviction')}: </span>
          <span style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
            {cache.evictionCount.toLocaleString()}
          </span>
        </div>
      </div>

      {/* 액션 버튼 */}
      <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginTop: 'auto' }}>
        <button
          type="button"
          onClick={onViewKeys}
          aria-label={`${cache.name} 키 보기`}
          style={{
            flex: 1,
            padding: 'var(--spacing-2)',
            background: 'var(--color-surface-elevated)',
            color: 'var(--color-text-primary)',
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 500,
            cursor: 'pointer',
            fontFamily: 'var(--font-family-sans)',
          }}
        >
          {t('cacheAdmin.button.viewKeys')}
        </button>

        <ButtonGuard requirePermission="CACHE_EVICT">
          {({ disabled, reason }) => (
            <button
              type="button"
              disabled={disabled}
              title={reason}
              onClick={onEvict}
              aria-label={`${cache.name} 캐시 비우기`}
              aria-describedby={disabled ? `evict-disabled-${cache.name}` : undefined}
              style={{
                flex: 1,
                padding: 'var(--spacing-2)',
                background: disabled ? 'var(--color-surface-muted)' : 'transparent',
                color: disabled ? 'var(--color-text-muted)' : 'var(--color-danger)',
                border: `1px solid ${disabled ? 'var(--color-surface-border)' : 'var(--color-danger)'}`,
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 500,
                cursor: disabled ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-family-sans)',
                opacity: disabled ? 0.6 : 1,
              }}
            >
              {t('cacheAdmin.button.evict')}
            </button>
          )}
        </ButtonGuard>
      </div>
    </article>
  );
}
